import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  NgZone,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { OemDealerCarousel, OpenService } from '@app-services/open.service';

export interface OemDealerSelection {
  name: string | null;
  id: string | null;
}

const RIBBON_SPEED_PX_PER_SECOND = 38;

const RESUME_DELAY_MS = 900;

const DRAG_THRESHOLD_PX = 6;

const MAX_FRAME_DELTA_MS = 100;

const COUNT_DURATION_MS = 900;

@Component({
  selector: 'app-oem-dealers-slider',
  templateUrl: './oem-dealers-slider.component.html',
  styleUrls: ['./oem-dealers-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OemDealersSliderComponent implements OnDestroy {

  readonly vehiclesAvailables = input<number>(0);
  readonly selectedOemDealerId = input<string>('');
  readonly isHome = input<boolean>(false);

  readonly clickSlider = output<OemDealerSelection>();

  private readonly viewportRef = viewChild<ElementRef<HTMLDivElement>>('viewport');
  private readonly trackRef = viewChild<ElementRef<HTMLDivElement>>('track');
  private readonly countRef = viewChild<ElementRef<HTMLSpanElement>>('count');

  private readonly openService = inject(OpenService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  protected readonly oemDealers = signal<OemDealerCarousel[]>([]);
  protected readonly canSee = computed(() => this.oemDealers().length > 0);

  protected readonly selectedDealer = computed(() => {
    const selectedId = this.selectedOemDealerId();
    return selectedId ? this.oemDealers().find(dealer => dealer.id === selectedId) ?? null : null;
  });

  protected readonly repetitions = signal(2);
  protected readonly sequences = computed(() => Array.from({ length: this.repetitions() }));

  protected readonly dragging = signal(false);

  private ribbonStarted = false;
  private sequenceWidth = 0;
  private offset = 0;
  private lastFrameTime = 0;
  private animationFrameId = 0;
  private hovering = false;
  private resumeAfter = 0;
  private dragPointerId: number | null = null;
  private dragLastX = 0;
  private dragDistance = 0;

  private countFrameId = 0;
  private displayedCount = 0;
  private readonly numberFormat = new Intl.NumberFormat('pt-BR');

  private resizeObserver?: ResizeObserver;
  private readonly listeners = new AbortController();

  constructor() {
    this.openService.getOemDealersCarousel()
      .pipe(takeUntilDestroyed())
      .subscribe(dealers => this.oemDealers.set(dealers ?? []));

    effect(() => {
      const viewport = this.viewportRef()?.nativeElement;
      const track = this.trackRef()?.nativeElement;

      if (!viewport || !track || this.ribbonStarted) {
        return;
      }

      this.ribbonStarted = true;
      untracked(() => this.startRibbon(viewport, track));
    }, { allowSignalWrites: true });

    effect(() => {
      const target = this.vehiclesAvailables();
      const element = this.countRef()?.nativeElement;

      if (!element) {
        return;
      }

      untracked(() => this.animateCount(element, target));
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationFrameId);
    cancelAnimationFrame(this.countFrameId);
    this.resizeObserver?.disconnect();
    this.listeners.abort();
  }

  protected isSelected(oemDealer: OemDealerCarousel): boolean {
    return !!this.selectedOemDealerId() && this.selectedOemDealerId() === oemDealer.id;
  }

  protected actionLabel(oemDealer: OemDealerCarousel): string {
    return this.isSelected(oemDealer)
      ? `Limpar filtro de ${oemDealer.name}`
      : `Ver veículos de ${oemDealer.name}`;
  }

  protected clearSelection(): void {
    this.clickSlider.emit({ name: null, id: null });
  }

  protected onDealerClick(oemDealer: OemDealerCarousel): void {
    if (this.dragDistance > DRAG_THRESHOLD_PX) {
      return;
    }

    if (oemDealer.redirectToFastQuote) {
      this.router.navigateByUrl(`/cotacao-rapida/veiculos?oemDealerIds=["${oemDealer.id}"]`);
      return;
    }

    if (this.isSelected(oemDealer)) {
      this.clickSlider.emit({ name: null, id: null });
      return;
    }

    this.clickSlider.emit({ name: oemDealer.name, id: oemDealer.id });
  }

  private startRibbon(viewport: HTMLDivElement, track: HTMLDivElement): void {
    this.zone.runOutsideAngular(() => {
      const { signal: abortSignal } = this.listeners;

      viewport.addEventListener('mouseenter', () => { this.hovering = true; }, { signal: abortSignal });
      viewport.addEventListener('mouseleave', () => { this.hovering = false; }, { signal: abortSignal });
      viewport.addEventListener('wheel', () => this.holdMovement(), { passive: true, signal: abortSignal });
      viewport.addEventListener('pointerdown', event => this.onPointerDown(event), { signal: abortSignal });

      window.addEventListener('pointermove', event => this.onPointerMove(event), { signal: abortSignal });
      window.addEventListener('pointerup', event => this.onPointerUp(event), { signal: abortSignal });
      window.addEventListener('pointercancel', event => this.onPointerUp(event), { signal: abortSignal });

      this.resizeObserver = new ResizeObserver(() => this.measure(viewport, track));
      this.resizeObserver.observe(viewport);
      this.resizeObserver.observe(track);

      this.measure(viewport, track);

      const step = (now: number) => {
        const delta = this.lastFrameTime ? Math.min(now - this.lastFrameTime, MAX_FRAME_DELTA_MS) : 0;
        this.lastFrameTime = now;

        if (!this.isPaused(now)) {
          this.offset += (RIBBON_SPEED_PX_PER_SECOND * delta) / 1000;
          this.render(track);
        }

        this.animationFrameId = requestAnimationFrame(step);
      };

      this.animationFrameId = requestAnimationFrame(step);
    });
  }

  private isPaused(now: number): boolean {
    return this.hovering
      || this.dragPointerId !== null
      || now < this.resumeAfter
      || this.prefersReducedMotion();
  }

  private animateCount(element: HTMLSpanElement, target: number): void {
    cancelAnimationFrame(this.countFrameId);

    const from = this.displayedCount;
    const distance = target - from;

    if (distance === 0 || this.prefersReducedMotion()) {
      this.displayedCount = target;
      element.textContent = this.numberFormat.format(target);
      return;
    }

    this.zone.runOutsideAngular(() => {
      const startedAt = performance.now();

      const step = (now: number) => {
        const progress = Math.min((now - startedAt) / COUNT_DURATION_MS, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        this.displayedCount = Math.round(from + distance * eased);
        element.textContent = this.numberFormat.format(this.displayedCount);

        if (progress < 1) {
          this.countFrameId = requestAnimationFrame(step);
        }
      };

      this.countFrameId = requestAnimationFrame(step);
    });
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  private measure(viewport: HTMLDivElement, track: HTMLDivElement): void {
    const firstSequence = track.firstElementChild as HTMLElement | null;

    if (!firstSequence) {
      return;
    }

    this.sequenceWidth = firstSequence.getBoundingClientRect().width;

    if (this.sequenceWidth <= 0) {
      return;
    }

    const needed = Math.max(2, Math.ceil(viewport.clientWidth / this.sequenceWidth) + 1);

    if (needed !== this.repetitions()) {
      this.zone.run(() => this.repetitions.set(needed));
    }

    this.render(track);
  }

  private render(track: HTMLDivElement): void {
    if (this.sequenceWidth > 0) {
      this.offset = ((this.offset % this.sequenceWidth) + this.sequenceWidth) % this.sequenceWidth;
    }

    track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
  }

  private holdMovement(): void {
    this.resumeAfter = performance.now() + RESUME_DELAY_MS;
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.dragPointerId !== null) {
      return;
    }

    this.dragPointerId = event.pointerId;
    this.dragLastX = event.clientX;
    this.dragDistance = 0;
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - this.dragLastX;
    this.dragLastX = event.clientX;
    this.dragDistance += Math.abs(deltaX);
    this.offset -= deltaX;

    const track = this.trackRef()?.nativeElement;

    if (track) {
      this.render(track);
    }

    if (this.dragDistance > DRAG_THRESHOLD_PX && !this.dragging()) {
      this.zone.run(() => this.dragging.set(true));
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    this.dragPointerId = null;
    this.holdMovement();

    if (this.dragging()) {
      this.zone.run(() => this.dragging.set(false));
    }

    setTimeout(() => { this.dragDistance = 0; });
  }
}
